import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "order-form-settings__body" }
const _hoisted_2 = { class: "order-form-settings__body-item order-form-settings__body-item--first" }
const _hoisted_3 = {
  class: "order-form-settings__label",
  for: "time-in-force"
}
const _hoisted_4 = {
  key: 0,
  class: "order-form-settings__body-item"
}
const _hoisted_5 = {
  class: "order-form-settings__label",
  for: "cancel-after"
}
const _hoisted_6 = { class: "order-form-settings__info" }
const _hoisted_7 = { class: "order-form-settings__info-text" }

import { computed, ref, type PropType, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { MarketModel } from '@exchange/libs/market/service/src';
import { TimeInForceType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { settingsService } from '@exchange/libs/settings/service/src';
import type { OrderAdvancedSettings } from '@exchange/libs/settings/service/src';


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderFormSettings',
  props: {
  market: { type: Object as PropType<MarketModel>, required: true },
},
  setup(__props) {

const { t } = useI18n({ useScope: 'global' });

const props = __props;

const orderAdvanced = computed(() => settingsService.settings.userSettings.orderAdvanced);
const advancedOn = ref(true);

const defaultTimeInForce = computed(() => ({
  value: TimeInForceType.GOOD_TILL_CANCELLED,
  label: t('modules.orderForm.advancedOptions.timeInForceNames.GOOD_TILL_CANCELLED'),
}));

const postOnlyTimeInForce = computed(() => ({
  value: TimeInForceType.POST_ONLY,
  label: t('modules.orderForm.advancedOptions.timeInForceNames.POST_ONLY'),
}));

const timeInForceOptions = computed(() => {
  if (props.market.state === 'POST_ONLY') {
    return [postOnlyTimeInForce.value];
  }

  return [
    defaultTimeInForce.value,
    {
      value: TimeInForceType.IMMEDIATE_OR_CANCELLED,
      label: t('modules.orderForm.advancedOptions.timeInForceNames.IMMEDIATE_OR_CANCELLED'),
    },
    {
      value: TimeInForceType.FILL_OR_KILL,
      label: t('modules.orderForm.advancedOptions.timeInForceNames.FILL_OR_KILL'),
    },
    // POST_ONLY is now a time in force option in the exchange, because you can only have GTC post only
    postOnlyTimeInForce.value,
  ];
});
const selectedTimeInForce = computed(() => {
  const tif = timeInForceOptions.value.find((e) => e.value === orderAdvanced.value.timeInForce);

  return tif || defaultTimeInForce.value;
});

const defaultCancelAfter = computed(() => ({
  value: 60,
  label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hour'),
}));
const cancelAfterOptions = computed(() => [
  {
    value: 1,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 1 }),
  },
  {
    value: 3,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 3 }),
  },
  {
    value: 5,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 5 }),
  },
  {
    value: 15,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 15 }),
  },
  {
    value: 30,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 30 }),
  },
  defaultCancelAfter.value,
  {
    value: 240,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hours', { number: 4 }),
  },
  {
    value: 360,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hours', { number: 6 }),
  },
  {
    value: 720,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hours', { number: 12 }),
  },
  {
    value: 1440,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.day'),
  },
  {
    value: 10080,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.week'),
  },
]);
const selectedCancelAfter = computed(() => {
  const ca = cancelAfterOptions.value.find((e) => e.value === orderAdvanced.value.cancelAfter);

  return ca || defaultCancelAfter.value;
});

const advancedHint = computed(() => {
  let info = '';

  switch (selectedTimeInForce.value.value) {
    case TimeInForceType.GOOD_TILL_CANCELLED:
    case TimeInForceType.IMMEDIATE_OR_CANCELLED:
      info = t('modules.orderForm.advancedOptions.executionHints.IMMEDIATE_OR_CANCELLED');
      break;
    case TimeInForceType.FILL_OR_KILL:
      info = t('modules.orderForm.advancedOptions.executionHints.FILL_OR_KILL');
      break;
    case TimeInForceType.POST_ONLY:
      info = t('modules.orderForm.advancedOptions.executionHints.POST_ONLY');
      break;
    default:
      break;
  }

  return {
    info,
    url: t('modules.orderForm.advancedOptions.hint.url'),
    urlText: t('modules.orderForm.advancedOptions.hint.text'),
  };
});

const setSettings = (path: string, value: unknown) => settingsService.setSettings({ path, value });
const updateSelectedTimeInForce = async (value: { value: TimeInForceType; label: string }) => {
  const timeInForce = value.value;
  const updatedSettings: Partial<OrderAdvancedSettings> = {
    ...orderAdvanced.value,
    cancelAfter: selectedCancelAfter.value.value,
    enabled: advancedOn.value,
    timeInForce,
  };

  await setSettings('orderAdvanced', updatedSettings);
};

const updateSelectedCancelAfter = async (value: { value: number; label: string }) => {
  const cancelAfter = value.value;
  const updatedSettings: Partial<OrderAdvancedSettings> = {
    ...orderAdvanced.value,
    cancelAfter,
    enabled: advancedOn.value,
  };

  await setSettings('orderAdvanced', updatedSettings);
};

watch(
  () => props.market,
  () => {
    if (props.market.state === 'POST_ONLY' && selectedTimeInForce.value.value !== TimeInForceType.POST_ONLY) {
      updateSelectedTimeInForce(postOnlyTimeInForce.value);
    }
  },
  {
    immediate: true,
  },
);

return (_ctx: any,_cache: any) => {
  const _component_ot_select = _resolveComponent("ot-select")!
  const _component_x_link_external = _resolveComponent("x-link-external")!
  const _component_ot_transition_collapse = _resolveComponent("ot-transition-collapse")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["order-form-settings", { 'order-form-settings--active': advancedOn.value }])
  }, [
    _createVNode(_component_ot_transition_collapse, { origin: "top center" }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("label", _hoisted_3, _toDisplayString(_ctx.$t('modules.orderForm.advancedOptions.timeInForce')), 1),
            _createVNode(_component_ot_select, {
              class: "order-form-settings__select",
              variant: "dark-outlined",
              "input-id": "time-in-force",
              "menu-top-space": true,
              label: "label",
              options: timeInForceOptions.value,
              value: selectedTimeInForce.value,
              "onUpdate:value": updateSelectedTimeInForce
            }, null, 8, ["options", "value"])
          ]),
          (_ctx.showCancelAfter)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("label", _hoisted_5, _toDisplayString(_ctx.$t('modules.orderForm.advancedOptions.cancelAfter')), 1),
                _createVNode(_component_ot_select, {
                  class: "order-form-settings__select cancel-after-select",
                  variant: "dark-outlined",
                  "input-id": "cancel-after",
                  label: "label",
                  "dropdown-max-height": "150px",
                  "menu-top-space": true,
                  options: cancelAfterOptions.value,
                  value: selectedCancelAfter.value,
                  "onUpdate:value": updateSelectedCancelAfter
                }, null, 8, ["options", "value"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("p", _hoisted_7, [
              _createTextVNode(_toDisplayString(advancedHint.value.info) + " ", 1),
              _createVNode(_component_x_link_external, {
                class: "order-form-settings__info-link",
                href: advancedHint.value.url,
                "use-system-browser": true
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(advancedHint.value.urlText), 1)
                ]),
                _: 1
              }, 8, ["href"])
            ])
          ])
        ], 512), [
          [_vShow, advancedOn.value]
        ])
      ]),
      _: 1
    })
  ], 2))
}
}

})